/**
 * Avalanche Report Handler
 * Handles: Daily Report (PDF) - Comprehensive 12-Section Format
 * PERFECTLY CENTERED ALIGNMENT & PROPER BOX SEPARATION FOR TEXT FIELDS
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';

const loadLogo = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateStr;
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr.replace(/-/g, '/');
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return '';
  }
};

const formatDateToDDMMYYYY_Dash = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateStr.replace(/\//g, '-');
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  }
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return '';
  }
};

const generateISTFilename = (prefix) => {
  const now = new Date();
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const istDate = istTime.toISOString().split('T')[0];
  const istTimeStr = istTime.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
  return `${prefix}_${istDate}_${istTimeStr}`;
};

// Returns empty string for 0 to leave blank spaces like the Web UI
const getSafeValue = (value) => {
  if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
      return '';
  }
  return String(value);
};

const getSafeStringValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return 'NA';
  }
  return String(value).trim();
};

export const handleAvalancheDailyReportPDF = async (message, format, rawData) => {
  if (format !== 'pdf') {
    alert('Avalanche Report only supports PDF format');
    return;
  }
  
  try {
    const doc = new jsPDF('p', 'pt', 'a4');
    
    const reportDate =
      rawData?.reported_on ||
      rawData?.reported_on_iso ||
      message.debug?.tool_result?.parameters?.reported_on ||
      message.debug?.parameters?.reported_on ||
      new Date().toISOString().split('T')[0];
    
    const displayDate = formatDateToDDMMYYYY(reportDate);
    const headerDisplayDate = formatDateToDDMMYYYY_Dash(reportDate);
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;
    
    // 1. RENDER LOGO
    let logoImg = null;
    try { logoImg = await loadLogo(govtLogo); } catch (e) {}
    if (logoImg) doc.addImage(logoImg, 'JPEG', centerX - 30, 40, 60, 60);
    
    // 2. RENDER HEADER
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 115, { align: 'center' });
    doc.text('Department Name', centerX, 135, { align: 'center' });
    doc.text(`Avalanche Daily Report (Dated on ${displayDate})`, centerX, 155, { align: 'center' });
    
    // 3. EXTRACT DATA
    let dailyRec = {};
    let cumRec = {};

    if (rawData?.fire_report && rawData.fire_report.length > 0) dailyRec = rawData.fire_report[0];
    else if (rawData?.avalanche_report && rawData.avalanche_report.length > 0) dailyRec = rawData.avalanche_report[0];
    
    if (rawData?.fire_report_cum && rawData.fire_report_cum.length > 0) cumRec = rawData.fire_report_cum[0];
    else if (rawData?.avalanche_report_cum && rawData.avalanche_report_cum.length > 0) cumRec = rawData.avalanche_report_cum[0];

    const districtName = String(dailyRec.dist_name || cumRec.dist_name || rawData?.district_name || '[DISTRICT]').toUpperCase();
    const blockName = dailyRec.block_name || cumRec.block_name || 'All Blocks';
    
    let finalAddress = getSafeStringValue(dailyRec.address || cumRec.address);
    if (rawData?.fire_report_address && rawData.fire_report_address.length > 0) {
      const validAddresses = rawData.fire_report_address.map(e => e.address).filter(a => a && a.trim() !== '');
      finalAddress = validAddresses.length > 0 ? validAddresses.join(', ') : 'NA';
    }

    // Helper to perfectly center values
    const valCell = (val) => ({ content: getSafeValue(val), halign: 'center' });

    // 4. TABLE DATA
    const tableData = [];
    
    // Top Metadata Row
    tableData.push([
      { content: `Name of the District: ${districtName}`, colSpan: 3, styles: { fontStyle: 'bold', halign: 'left' } },
      { content: `Date:\n${headerDisplayDate}`, colSpan: 2, styles: { fontStyle: 'bold', halign: 'left' } }
    ]);
    
    // ✅ FIX: Text fields split into Label Box and Value Box
    tableData.push([ '1', 'Block', { content: blockName, colSpan: 3 } ]);
    
    tableData.push([
      { content: '2', rowSpan: 2 },
      { content: 'Number of incidents (Today)', colSpan: 2 },
      { content: getSafeValue(dailyRec.no_of_incidents_today || dailyRec.no_of_incidents), colSpan: 2, halign: 'center' }
    ]);
    tableData.push([ 
      { content: 'Number of incidents (Cumulative)', colSpan: 2 }, 
      { content: getSafeValue(cumRec.no_of_incidents_cum), colSpan: 2, halign: 'center' } 
    ]);
    
    // ✅ FIX: Separated into two boxes
    tableData.push([ '3', 'Addresses of the places of incidents', { content: finalAddress, colSpan: 3 } ]);
    
    tableData.push([
      { content: '4', rowSpan: 4 },
      { content: 'Number of deaths.', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }
    ]);
    tableData.push([ { content: 'Human', colSpan: 2 }, valCell(dailyRec.no_of_human_deaths_today), valCell(cumRec.no_of_human_deaths_cum) ]);
    tableData.push([ { content: 'Cattle', colSpan: 2 }, valCell(dailyRec.no_of_cattle_deaths_today), valCell(cumRec.no_of_cattle_deaths_cum) ]);
    tableData.push([ { content: 'Poultry', colSpan: 2 }, valCell(dailyRec.no_of_poultry_deaths_today), valCell(cumRec.no_of_poultry_deaths_cum) ]);
    
    tableData.push([
      { content: '5', rowSpan: 4 },
      { content: 'Number of injured', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }
    ]);
    tableData.push([ { content: 'Human', colSpan: 2 }, valCell(dailyRec.no_of_human_injured_today), valCell(cumRec.no_of_human_injured_cum) ]);
    tableData.push([ { content: 'Cattle', colSpan: 2 }, valCell(dailyRec.no_of_cattle_injured_today), valCell(cumRec.no_of_cattle_injured_cum) ]);
    tableData.push([ { content: 'Poultry', colSpan: 2 }, valCell(dailyRec.no_of_poultry_injured_today), valCell(cumRec.no_of_poultry_injured_cum) ]);
    
    tableData.push([
      { content: '6', rowSpan: 4 },
      { content: 'Ex-gratia details', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }
    ]);
    tableData.push([ { content: 'Number of deceased for whom payment of ex-gratia done', colSpan: 2 }, valCell(dailyRec.no_of_deceased_pay_exgra_done_today), valCell(cumRec.no_of_deceased_pay_exgra_done_cum) ]);
    tableData.push([ { content: 'Number of dead cattles for which payment done', colSpan: 2 }, valCell(dailyRec.no_of_dead_cattles_pay_done_today), valCell(cumRec.no_of_dead_cattles_pay_done_cum) ]);
    tableData.push([ { content: 'Number of dead poultry for which payment done', colSpan: 2 }, valCell(dailyRec.no_of_dead_poultry_pay_done_today), valCell(cumRec.no_of_dead_poultry_pay_done_cum) ]);
    
    tableData.push([
      { content: '7', rowSpan: 7 },
      { content: 'Number of damaged houses', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }
    ]);
    tableData.push([ { content: 'Concrete', rowSpan: 2 }, 'Complete', valCell(dailyRec.concrete_complete), valCell(cumRec.concrete_complete_cum) ]);
    tableData.push([ 'Partial', valCell(dailyRec.concrete_partial), valCell(cumRec.concrete_partial_cum) ]);
    tableData.push([ { content: 'Kutcha', rowSpan: 2 }, 'Complete', valCell(dailyRec.kutcha_complete), valCell(cumRec.kutcha_complete_cum) ]);
    tableData.push([ 'Partial', valCell(dailyRec.kutcha_partial), valCell(cumRec.kutcha_partial_cum) ]);
    tableData.push([ { content: 'Cottage/Hut', colSpan: 2 }, valCell(dailyRec.cottage_hut), valCell(cumRec.cottage_hut_cum) ]);
    tableData.push([ { content: 'Total number of damaged houses', colSpan: 2 }, valCell(dailyRec.total), valCell(cumRec.total_cum) ]);

    tableData.push([
      { content: '8', rowSpan: 7 },
      { content: 'Number of damaged houses for which payment done', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }
    ]);
    tableData.push([ { content: 'Concrete', rowSpan: 2 }, 'Complete', valCell(dailyRec.concrete_complete_pay_done_today), valCell(cumRec.concrete_complete_pay_done_cum) ]);
    tableData.push([ 'Partial', valCell(dailyRec.concrete_partial_pay_done_today), valCell(cumRec.concrete_partial_pay_done_cum) ]);
    tableData.push([ { content: 'Kutcha', rowSpan: 2 }, 'Complete', valCell(dailyRec.kutcha_complete_pay_done_today), valCell(cumRec.kutcha_complete_pay_done_cum) ]);
    tableData.push([ 'Partial', valCell(dailyRec.kutcha_partial_pay_done_today), valCell(cumRec.kutcha_partial_pay_done_cum) ]);
    tableData.push([ { content: 'Cottage/Hut', colSpan: 2 }, valCell(dailyRec.cottage_hut_pay_done_today), valCell(cumRec.cottage_hut_pay_done_cum) ]);
    tableData.push([ { content: 'Total number of damaged houses for which payment done', colSpan: 2 }, valCell(dailyRec.today_pay_done_today), valCell(cumRec.today_pay_done_cum) ]);

    tableData.push([
      { content: '9', rowSpan: 4 },
      { content: 'Cattle shed details and Compensation for damaged house/hut/cattle shed', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }
    ]);
    tableData.push([ { content: 'Number of damaged cattle sheds', colSpan: 2 }, valCell(dailyRec.no_of_dam_cattle_sheds_today), valCell(cumRec.no_of_dam_cattle_sheds_cum) ]);
    tableData.push([ { content: 'Number of damaged cattle sheds for which payment done', colSpan: 2 }, valCell(dailyRec.no_of_dam_cattle_sheds_pay_done_today), valCell(cumRec.no_of_dam_cattle_sheds_pay_done_cum) ]);
    tableData.push([ { content: 'Amount paid against damaged house/hut/cattle shed (Rs.)', colSpan: 2 }, valCell(dailyRec.amount_paid_dam_hous_hut_cattle_today), valCell(cumRec.amount_paid_dam_hous_hut_cattle_cum) ]);

    // ✅ FIX: Separated into two boxes
    tableData.push([
      { content: '10', rowSpan: 4 },
      'Crop Type',
      { content: getSafeStringValue(dailyRec.damaged_crop_type || cumRec.damaged_crop_type || dailyRec.crop_type), colSpan: 3 }
    ]);
    tableData.push([ { content: 'Damaged Crop', colSpan: 2, styles: { fontStyle: 'bold' } }, { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }, { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } } ]);
    tableData.push([ { content: 'Area of damaged crop (in acre)', colSpan: 2 }, valCell(dailyRec.damaged_crop_area_affected), valCell(cumRec.damaged_crop_area_affected_cum) ]);
    tableData.push([ { content: 'Estimated value of damaged crops (Rs.)', colSpan: 2 }, valCell(dailyRec.estimated_value_of_damaged_crops_today), valCell(cumRec.estimated_value_of_damaged_crops_cum) ]);

    tableData.push([
      { content: '11', rowSpan: 7 },
      { content: 'Relief Distribution', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 240, 240] } }
    ]);
    tableData.push([ { content: 'Amount paid under the agriculture input subsidy (Rs)', colSpan: 2 }, valCell(dailyRec.amount_paid_agricultural_input_sub_today), valCell(cumRec.amount_paid_agricultural_input_sub_cum) ]);
    tableData.push([ { content: 'Area of crop for which agriculture input subsidy paid (in acre)', colSpan: 2 }, valCell(dailyRec.area_crop_agricultural_input_sub_paid_today), valCell(cumRec.area_crop_agricultural_input_sub_paid_cum) ]);
    tableData.push([ { content: 'Total amount of GR paid @Rs. 7000 per family', colSpan: 2 }, valCell(dailyRec.total_amount_gr_paid_7k_per_fam_today), valCell(cumRec.total_amount_gr_paid_7k_per_fam_cum) ]);
    tableData.push([ { content: 'Total amount paid for cloth/utensils etc., @5000 per family', colSpan: 2 }, valCell(dailyRec.total_amount_cloth_paid_5k_per_fam_today), valCell(cumRec.total_amount_cloth_paid_5k_per_fam_cum) ]);
    tableData.push([ { content: 'Number of polythene sheets distributed', colSpan: 2 }, valCell(dailyRec.number_of_polythene_sheets_distributed_today), valCell(cumRec.number_of_polythene_sheets_distributed_cum) ]);
    tableData.push([ { content: 'Quantity of dry ration distributed (Kg)', colSpan: 2 }, valCell(dailyRec.quantity_dry_ration_distributed_today), valCell(cumRec.quantity_dry_ration_distributed_cum) ]);

    // ✅ FIX: Separated into two boxes
    tableData.push([ 
      '12', 
      'Remarks', 
      { content: getSafeStringValue(dailyRec.remarks || cumRec.remarks), colSpan: 3 } 
    ]);

    // 5. PERFECTLY CENTERED AUTO-TABLE
    autoTable(doc, {
      body: tableData,
      startY: 180,
      theme: 'grid',
      // Margins perfectly center the 525-width table on the 595.28-width page
      margin: { left: 35, right: 35 }, 
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' }, // S.No
        1: { cellWidth: 140, halign: 'left' },  // Main Category
        2: { cellWidth: 160, halign: 'left' },  // Sub Category
        3: { cellWidth: 100, halign: 'center' }, // Today
        4: { cellWidth: 100, halign: 'center' }  // Cumulative
      },
      bodyStyles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        font: 'Helvetica',
        fontSize: 9,
        cellPadding: 4,
        valign: 'middle'
      },
      didParseCell: function (data) {
        // Enforce custom inline styles cleanly
        if (data.cell.raw && typeof data.cell.raw === 'object' && data.cell.raw.styles) {
            Object.assign(data.cell.styles, data.cell.raw.styles);
        }
        
        // Auto-bold the section numbers (1-12) and their corresponding titles
        const rowRaw = data.row.raw;
        const firstVal = typeof rowRaw[0] === 'object' ? rowRaw[0].content : rowRaw[0];
        
        if (['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].includes(String(firstVal))) {
            if (data.column.index === 0 || data.column.index === 1) {
                data.cell.styles.fontStyle = 'bold';
            }
        }
      }
    });
    
    // 6. FOOTER
    const finalY = doc.lastAutoTable.finalY || 200;
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY = Math.max(finalY + 60, pageHeight - 120);

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    doc.text('Letter No.: ', 35, footerY);
    doc.text('Date: ', 35, footerY + 20);
    doc.text('Copy: ', 35, footerY + 40);
    
    doc.text('Signature of District Officers/ ', 330, footerY);
    doc.text('Additional District Magistrate(ADM)(Disaster Management)/', 330, footerY + 20);
    doc.text('Officer in Charge(District Disaster Management)/', 330, footerY + 40);
    doc.setFont('Helvetica', 'bold');
    doc.text(`[${districtName} DISTRICT]`, 330, footerY + 60);
    
    doc.save(generateISTFilename('avalanche_daily_report') + '.pdf');
    
  } catch (error) {
    console.error('❌ Avalanche PDF Error:', error);
    alert(`PDF Generation Error: ${error.message}`);
  }
};

export const isAvalancheReport = (toolName) => {
    return ['avalanche_daily_report', 'avalanchedailyreport', 'avalanche_daily_report_form'].some(n => toolName?.toLowerCase().includes(n));
};

export const isAvalancheStatus = (toolName) => {
    return toolName?.toLowerCase().includes('avalanche_daily_report_status');
};

export const isAvalancheMIS = (toolName) => {
    return toolName?.toLowerCase().includes('avalanche_mis');
};